import { formatDurationHuman } from "../../../src/infra/format-time/format-duration.ts";
import { formatRelativeTimestamp } from "../../../src/infra/format-time/format-relative.ts";
import { stripReasoningTagsFromText } from "../../../src/shared/text/reasoning-tags.js";

export { formatRelativeTimestamp, formatDurationHuman };

export function formatMs(ms?: number | null): string {
  if (!ms && ms !== 0) {
    return "n/a";
  }
  return new Date(ms).toLocaleString();
}

export function formatList(values?: Array<string | null | undefined>): string {
  if (!values || values.length === 0) {
    return "none";
  }
  return values.filter((v): v is string => Boolean(v && v.trim())).join(", ");
}

export function clampText(value: string, max = 120): string {
  if (value.length <= max) {
    return value;
  }
  return `${value.slice(0, Math.max(0, max - 1))}…`;
}

export function truncateText(
  value: string,
  max: number,
): {
  text: string;
  truncated: boolean;
  total: number;
} {
  if (value.length <= max) {
    return { text: value, truncated: false, total: value.length };
  }
  return {
    text: value.slice(0, Math.max(0, max)),
    truncated: true,
    total: value.length,
  };
}

export function toNumber(value: string, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function parseList(input: string): string[] {
  return input
    .split(/[,\n]/)
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}

export function stripThinkingTags(value: string): string {
  return stripReasoningTagsFromText(value, { mode: "preserve", trim: "start" });
}

/**
 * Strip plugin-injected context tags (e.g. `<emotional-context>…</emotional-context>`)
 * that are prepended to user messages via the `prependContext` hook.
 * Only removes leading XML-like blocks so user-authored content is preserved.
 */
const LEADING_PLUGIN_CONTEXT_RE = /^(?:<([\w-]+)>\s*[\s\S]*?<\/\1>\s*)+/;

export function stripPluginContext(value: string): string {
  return value.replace(LEADING_PLUGIN_CONTEXT_RE, "");
}

/**
 * Detect when an assistant message is entirely a raw JSON tool-result payload
 * (e.g. search results, tool errors) and strip it.  These payloads are already
 * surfaced via tool cards; showing them again as raw text clutters the chat.
 *
 * Returns `null` to signal the message should be hidden entirely.
 */
const TOOL_JSON_KEYS = new Set([
  "tool",
  "status",
  "tookMs",
  "externalContent",
  "untrusted",
  "source",
  "provider",
]);

export function stripRawToolOutput(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return value;
  try {
    const obj = JSON.parse(trimmed);
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) return value;
    const keys = Object.keys(obj);
    const hits = keys.filter((k) => TOOL_JSON_KEYS.has(k));
    if (hits.length >= 2) return null;
    return value;
  } catch {
    return value;
  }
}
